import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMemberAuth } from '../../../contexts/MemberAuthContext';
import { mfetch, MemberApiError, PlanDayRow } from '../api';
import Icon, { IconName } from '../design/Icon';
import { Sheet } from '../design/atoms';
import { EXERCISE_LIBRARY, FOCUS_GROUPS, LibraryExercise, allExercises } from '../plan/exerciseLibrary';
import { WORKOUT_TYPES, WorkoutType, fmtTarget } from '../plan/planShared';

/* "Start an activity" — the walk-in path into logging. Pick a ready-made
   session by focus (or build one from the library) and the server materializes
   today's plan day so the session screen can log against it right away. */

const TYPE_ICON: Record<WorkoutType, IconName> = {
  strength: 'dumbbell',
  body: 'user',
  run: 'spark',
  erg: 'flame',
  circuit: 'sliders',
  hold: 'clock',
  flow: 'leaf',
};

const CUSTOM_FOCUS = 'My session';

export default function MemberActivityPage() {
  const { token } = useMemberAuth();
  const navigate = useNavigate();

  const [picked, setPicked] = useState<string | null>(null); // focus group, or CUSTOM_FOCUS
  const [custom, setCustom] = useState<LibraryExercise[]>([]);
  const [sheet, setSheet] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const catalogue = useMemo(() => allExercises(), []);
  const isCustom = picked === CUSTOM_FOCUS;
  const items = isCustom ? custom : picked ? EXERCISE_LIBRARY[picked] ?? [] : [];
  const canStart = items.length > 0;

  const start = () => {
    if (!token || !canStart) return;
    setBusy(true);
    setError(null);
    mfetch<{ data: PlanDayRow }>(token, '/member/plan/days', {
      method: 'POST',
      body: {
        focus: isCustom ? CUSTOM_FOCUS : picked,
        items: items.map(e => ({ exerciseName: e.name, workoutType: e.workoutType, target: e.target })),
      },
    })
      .then(r => navigate(`/app/session/${r.data.date}`, { replace: true }))
      .catch(e => {
        setError(e instanceof MemberApiError ? e.message : 'Could not start this session');
        setBusy(false);
      });
  };

  return (
    <div className="mpage">
      <div className="row" style={{ paddingTop: 8, gap: 10 }}>
        <button className="icon-btn" aria-label="Back to progress" onClick={() => navigate('/app/progress')}>
          <Icon n="left" s={18} c="var(--ink-2)" />
        </button>
        <div className="col" style={{ gap: 1, minWidth: 0 }}>
          <div className="dp dp-m">Start an activity</div>
          <div className="tiny">Pick a session, then log as you go</div>
        </div>
      </div>

      {FOCUS_GROUPS.map(group => {
        const seeds = EXERCISE_LIBRARY[group] ?? [];
        const on = picked === group;
        return (
          <div key={group} className={`card${on ? ' tint' : ''}`}>
            <button
              className="row"
              style={{ width: '100%', textAlign: 'left' }}
              onClick={() => setPicked(on ? null : group)}
            >
              <div className={`li${on ? ' gold' : ''}`}>
                <Icon n={TYPE_ICON[seeds[0]?.workoutType ?? 'strength']} s={17} c={on ? 'var(--gold-2)' : 'var(--ink-2)'} />
              </div>
              <div className="col" style={{ gap: 2, flex: 1, minWidth: 0 }}>
                <div className="mid trunc">{group}</div>
                <div className="tiny trunc">
                  {seeds.length} exercises · {seeds.slice(0, 3).map(e => e.name).join(', ')}…
                </div>
              </div>
              <Icon n={on ? 'check' : 'right'} s={16} c={on ? 'var(--gold-2)' : 'var(--ink-3)'} />
            </button>

            {on && (
              <>
                <div className="divider" style={{ margin: '12px 0' }} />
                <div className="col" style={{ gap: 8 }}>
                  {seeds.map(e => (
                    <div key={e.name} className="between">
                      <span className="sub trunc">{e.name}</span>
                      <span className="tiny nw">{fmtTarget(e.target)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        );
      })}

      {/* Build your own */}
      <div className={`card${isCustom ? ' tint' : ''}`}>
        <button
          className="row"
          style={{ width: '100%', textAlign: 'left' }}
          onClick={() => { setPicked(CUSTOM_FOCUS); setSheet(true); }}
        >
          <div className={`li${isCustom ? ' gold' : ''}`}>
            <Icon n="plus" s={17} c={isCustom ? 'var(--gold-2)' : 'var(--ink-2)'} />
          </div>
          <div className="col" style={{ gap: 2, flex: 1, minWidth: 0 }}>
            <div className="mid trunc">Build your own</div>
            <div className="tiny trunc">
              {custom.length ? `${custom.length} exercises picked` : 'Choose from the exercise library'}
            </div>
          </div>
          <Icon n="right" s={16} c="var(--ink-3)" />
        </button>

        {isCustom && custom.length > 0 && (
          <>
            <div className="divider" style={{ margin: '12px 0' }} />
            <div className="col" style={{ gap: 8 }}>
              {custom.map(e => (
                <div key={e.name} className="between">
                  <div className="col" style={{ gap: 1, minWidth: 0 }}>
                    <span className="sub trunc">{e.name}</span>
                    <span className="tiny trunc">{fmtTarget(e.target)}</span>
                  </div>
                  <button
                    className="icon-btn"
                    aria-label={`Remove ${e.name}`}
                    onClick={() => setCustom(c => c.filter(x => x.name !== e.name))}
                  >
                    <Icon n="minus" s={16} c="var(--ink-2)" />
                  </button>
                </div>
              ))}
              <button className="btn ghost sm" onClick={() => setSheet(true)}>＋ Add another</button>
            </div>
          </>
        )}
      </div>

      {error && <div className="card flat"><div className="sub wrap">{error}</div></div>}

      <button className="btn gold block" disabled={!canStart || busy} onClick={start}>
        {busy ? 'Starting…' : 'Start session →'}
      </button>

      {sheet && (
        <AddExerciseSheet
          catalogue={catalogue}
          picked={custom}
          onAdd={e => setCustom(c => (c.some(x => x.name === e.name) ? c : [...c, e]))}
          onClose={() => setSheet(false)}
        />
      )}
    </div>
  );
}

// ── Exercise picker ──────────────────────────────────────────────────────────
function AddExerciseSheet({
  catalogue,
  picked,
  onAdd,
  onClose,
}: {
  catalogue: LibraryExercise[];
  picked: LibraryExercise[];
  onAdd: (e: LibraryExercise) => void;
  onClose: () => void;
}) {
  const [group, setGroup] = useState<string>(FOCUS_GROUPS[0]);
  const [q, setQ] = useState('');

  const list = useMemo(() => {
    const source = q.trim() ? catalogue : EXERCISE_LIBRARY[group] ?? [];
    const needle = q.trim().toLowerCase();
    return source
      .filter(e => !picked.some(p => p.name === e.name))
      .filter(e => !needle || e.name.toLowerCase().includes(needle))
      .slice(0, 25);
  }, [catalogue, group, q, picked]);

  return (
    <Sheet onClose={onClose}>
      <div className="col" style={{ gap: 2 }}>
        <div className="lbl">Exercises</div>
        <div className="dp dp-m">Add an exercise</div>
      </div>

      <input
        className="minput"
        placeholder="Search exercises"
        value={q}
        onChange={e => setQ(e.target.value)}
      />

      {!q.trim() && (
        <div className="hscroll">
          {FOCUS_GROUPS.map(g => (
            <button key={g} className={`chip${g === group ? ' on' : ''}`} onClick={() => setGroup(g)}>{g}</button>
          ))}
        </div>
      )}

      <div className="col" style={{ maxHeight: 320, overflowY: 'auto' }}>
        {list.length === 0 ? (
          <div className="sub wrap" style={{ padding: '8px 0' }}>
            Everything from this group is already in the day.
          </div>
        ) : (
          list.map(e => (
            <button key={e.name} className="lrow" onClick={() => onAdd(e)}>
              <div className="li"><Icon n={TYPE_ICON[e.workoutType]} s={16} c="var(--ink-2)" /></div>
              <div className="col" style={{ gap: 1, minWidth: 0 }}>
                <span className="mid trunc">{e.name}</span>
                <span className="tiny trunc">{WORKOUT_TYPES[e.workoutType].label} · {fmtTarget(e.target)}</span>
              </div>
              <Icon n="plus" s={18} c="var(--gold-2)" />
            </button>
          ))
        )}
      </div>

      <button className="btn block" onClick={onClose}>Done</button>
    </Sheet>
  );
}
